import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ClockIcon, TableCellsIcon, CircleStackIcon } from '@heroicons/react/24/outline';

/**
 * SchemaMetadataCard - Rich metadata card for schema display
 *
 * Features:
 * - Schema name and description
 * - Key statistics (table count, rows, size)
 * - Tier status badges
 * - Last updated timestamp
 * - Selection checkbox
 * - Hover effects
 *
 * @param {Object} props
 * @param {Object} props.schema - Schema object with metadata
 * @param {boolean} props.isSelected - Whether schema is selected
 * @param {function} props.onToggleSelection - Callback for selection toggle
 * @param {boolean} props.showMetadata - Whether to show detailed metadata
 * @param {boolean} props.selectable - Whether card is selectable (default: true)
 */
const SchemaMetadataCard = ({
  schema,
  isSelected = false,
  onToggleSelection,
  showMetadata = true,
  selectable = true
}) => {
  const handleClick = () => {
    if (selectable && onToggleSelection) {
      onToggleSelection();
    }
  };

  const handleKeyPress = (e) => {
    if (selectable && onToggleSelection && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggleSelection();
    }
  };

  // Format bytes helper
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (!num && num !== 0) return 'N/A';
    return num.toLocaleString();
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  // Get tier status badge
  const getTierBadge = (tier) => {
    if (!tier) return null;

    const tierColors = {
      1: 'bg-blue-100 text-blue-800',
      2: 'bg-green-100 text-green-800',
      3: 'bg-yellow-100 text-yellow-800',
      4: 'bg-purple-100 text-purple-800',
      5: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${tierColors[tier] || 'bg-gray-100 text-gray-800'}`}>
        Tier {tier}
      </span>
    );
  };

  // Calculate card classes
  const cardClasses = `
    relative rounded-lg transition-all duration-200 p-4
    ${isSelected
      ? 'border-4 border-blue-600 bg-blue-100 shadow-md'
      : 'border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
    }
    ${selectable ? 'cursor-pointer' : 'cursor-default'}
  `;

  return (
    <div
      className={cardClasses}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      role={selectable ? 'button' : 'article'}
      tabIndex={selectable ? 0 : undefined}
      aria-pressed={selectable ? isSelected : undefined}
    >
      {/* Selection Indicator */}
      {selectable && (
        <div className="absolute top-3 right-3">
          {isSelected ? (
            <CheckCircleIcon className="h-7 w-7 text-blue-600" />
          ) : (
            <div className="h-6 w-6 rounded-full border-2 border-gray-300 bg-white" />
          )}
        </div>
      )}

      {/* Schema Header */}
      <div className="pr-8">
        <h3 className="text-lg font-semibold text-gray-900 truncate" title={schema.name}>
          {schema.name}
        </h3>
        {schema.description && (
          <p className="mt-1 text-sm text-gray-500 line-clamp-2" title={schema.description}>
            {schema.description}
          </p>
        )}
      </div>

      {/* No Cache Warning */}
      {schema._no_cache && (
        <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-xs text-amber-800">
            No tables discovered yet. Select this schema to include it in metadata collection.
          </p>
        </div>
      )}

      {/* Tier Status Badge */}
      {schema.current_tier && (
        <div className="mt-3">
          {getTierBadge(schema.current_tier)}
        </div>
      )}

      {/* Metadata Section */}
      {showMetadata && (
        <div className="mt-4 space-y-2">
          {/* Table Count */}
          <div className="flex items-center text-sm text-gray-600">
            <TableCellsIcon className="h-4 w-4 mr-2 text-gray-400" />
            <span className="font-medium">{formatNumber(schema.table_count || 0)}</span>
            <span className="ml-1">
              {schema.table_count === 1 ? 'table' : 'tables'}
            </span>
          </div>

          {/* Total Rows */}
          {schema.total_rows !== undefined && schema.total_rows !== null && (
            <div className="flex items-center text-sm text-gray-600">
              <CircleStackIcon className="h-4 w-4 mr-2 text-gray-400" />
              <span className="font-medium">{formatNumber(schema.total_rows)}</span>
              <span className="ml-1">rows</span>
            </div>
          )}

          {/* Size */}
          {schema.size_bytes !== undefined && schema.size_bytes !== null && (
            <div className="flex items-center text-sm text-gray-600">
              <CircleStackIcon className="h-4 w-4 mr-2 text-gray-400" />
              <span className="font-medium">{formatBytes(schema.size_bytes)}</span>
            </div>
          )}

          {/* Last Updated */}
          {schema.last_updated && (
            <div className="flex items-center text-sm text-gray-500">
              <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
              <span>Updated {formatTimestamp(schema.last_updated)}</span>
            </div>
          )}
        </div>
      )}

      {/* Hover State Indicator */}
      {selectable && !isSelected && (
        <div className="absolute inset-0 rounded-lg border-2 border-transparent hover:border-primary-200 transition-colors duration-200 pointer-events-none" />
      )}
    </div>
  );
};

export default SchemaMetadataCard;
